import { useState, useEffect, forwardRef, useImperativeHandle, useRef, Ref } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { GenericRadioFilter, ListOption } from '@hotjar/global-filters';
import { Tooltip, Popover, tokens } from '@hotjar/hotsauce';

import type { HotjarFilters } from '@hotjar/recordings/src/store/list/types';
import {
  HotjarFilterOptionAll,
  HotjarFilterOptionNotWatched,
  HotjarFilterOptionWatched,
  WatchedHotjarFilter,
} from '@hotjar/recordings/src/store/list/types';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';

const Container = styled.div`
  width: fit-content;
  margin-top: -${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x3};
`;

export type WatchedRecordingsFilterHandle = {
  focusWatchedRecordingsSelect: () => void;
};

type WatchedRecordingsFilterProps = {
  onHotjarFiltersUpdated: (hotjarFilters: HotjarFilters) => void;
  hotjarFilters: HotjarFilters;
  showNotWatchedPopoverInfo?: boolean;
  onNotWatchedPopoverInfoClosed?: () => void;
};

const InternalWatchedRecordingsFilter = (
  {
    onHotjarFiltersUpdated,
    hotjarFilters,
    showNotWatchedPopoverInfo = false,
    onNotWatchedPopoverInfoClosed,
  }: WatchedRecordingsFilterProps,
  ref: Ref<WatchedRecordingsFilterHandle>,
): JSX.Element => {
  const { t } = useTranslation();
  const [allowTooltip, setAllowTooltip] = useState(true);
  const filterContainerRef = useRef<HTMLSelectElement>(null);
  const { data: currentSite } = useCurrentSite();
  const { click } = useTrack();

  const [watchedRecordingsFilter, setWatchedRecordingsFilter] = useState(hotjarFilters.watched);

  useEffect(() => {
    setWatchedRecordingsFilter(hotjarFilters.watched);
  }, [hotjarFilters]);

  const WATCHED_FILTER_OPTIONS: ListOption<WatchedHotjarFilter>[] = [
    {
      value: HotjarFilterOptionAll,
      label: t('common:recordings.list-actions.watched-recordings.all'),
    },
    {
      value: HotjarFilterOptionNotWatched,
      label: t('common:recordings.list-actions.watched-recordings.not_watched'),
    },
    {
      value: HotjarFilterOptionWatched,
      label: t('common:recordings.list-actions.watched-recordings.watched'),
    },
  ];

  const onSelectChange = (value: string | undefined) => {
    let label;

    onHotjarFiltersUpdated({ ...hotjarFilters, watched: value as WatchedHotjarFilter });

    switch (value) {
      case HotjarFilterOptionAll:
        label = 'all-recordings';
        break;
      case HotjarFilterOptionNotWatched:
        label = 'not-watched';
        break;
      case HotjarFilterOptionWatched:
        label = 'already-watched';
        break;
      default:
        break;
    }

    click(`checked_${label}`, `recordings.actions.filter_${label}`, {
      account_id: currentSite?.account?.id,
      organization_id: currentSite?.organization?.id,
      site_id: currentSite?.id,
    });
  };

  useImperativeHandle(ref, () => ({
    focusWatchedRecordingsSelect() {
      filterContainerRef?.current?.getElementsByTagName('button')[0]?.focus();
    },
  }));

  return (
    <Popover
      placement="bottom-start"
      title={t(`common:recordings.list-actions.popover.title`)}
      icon="info"
      text={t(`common:recordings.list-actions.popover.message`)}
      visible={showNotWatchedPopoverInfo}
      appendTo="parent"
      onHide={onNotWatchedPopoverInfoClosed}
      zIndex={20}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: If visible is passed, trigger needs to be undefined
      trigger={undefined}
    >
      <Tooltip
        ref={filterContainerRef}
        isDisabled={!allowTooltip}
        maxWidth={200}
        placement="bottom"
        text={t(
          `common:recordings.list-actions.watched-recordings.${watchedRecordingsFilter}_tooltip`,
        )}
      >
        <Container>
          <GenericRadioFilter
            filterName="watched"
            label2={t(`common:recordings.list-actions.watched-recordings.${hotjarFilters.watched}`)}
            iconName="hidden"
            isInactive={hotjarFilters.watched === HotjarFilterOptionAll}
            currentValue={hotjarFilters.watched}
            onApply={onSelectChange}
            optionList={WATCHED_FILTER_OPTIONS}
            onDropdownShown={setAllowTooltip.bind(null, false)}
            onDropdownHidden={setAllowTooltip.bind(null, true)}
            showDropdownCaret
          />
        </Container>
      </Tooltip>
    </Popover>
  );
};

export const WatchedRecordingsFilter = forwardRef<
  WatchedRecordingsFilterHandle,
  WatchedRecordingsFilterProps
>(InternalWatchedRecordingsFilter);
